/**
 * A wrapper for bootbox dialogs
 */
namespace appn.dialog {
    export enum MediaType {
        Audio = 'Audio',
        Image = 'Image',
        Video = 'Video',
    }

    /**
     * Show Alert dialog
     */
    export function alert(arg?: any) {
        appn.dialog.hide();
        bootbox.alert.apply(null, arguments as any);
    }
     
    /**
     * Show Confirm dialog
     */
    export function confirm(arg?: any) {
        appn.dialog.hide();
        bootbox.confirm.apply(null, arguments as any);
    }

    /**
     * Show Prompt dialog
     */
    export function prompt(arg?: any) {
        appn.dialog.hide();
        bootbox.prompt.apply(null, arguments as any);
    }

    /**
     * Show custom dialog
     */
    export function custom(arg?: any) {
        appn.dialog.hide();
        bootbox.dialog.apply(null, arguments as any);
    }

    /**
     * Displays images, audio and video in a dialog.
     * Use when an element with 'data-' properties cannot be passed in
     */
    export function viewMedia(url: string, mediaName: string, mediaType: MediaType, downloadBtn: boolean = true) {
        let msgHtml: string = appn.emptyString;
        let boxTitle: string = appn.emptyString;
        let name: string = mediaName;

        switch (mediaType) {
            case MediaType.Audio:
                name = !name ? T('Audio Download') : name;
                boxTitle = `<i class="fa fa-volume"></i> ${name}`;
                msgHtml = `<audio id="popupAudio" controls>` +
                    `<source src="${url}" type = "audio/mpeg" >` +
                    `<source src="${url}" type="audio/wav" ></audio>`;
                break;
            case MediaType.Video:
                name = !name ? T('Video Download') : name;
                boxTitle = `<i class="fa fa-video"></i> ${name}`;
                msgHtml = `<video id="popupVideo" controls preload="auto">` +
                    `<source src="${url}" type = "video/mp4" ></video>`;
                break;
            default:
                name = !name ? T('Image Download') : name;
                boxTitle = `<i class="fa fa-image"></i> ${name}`;
                msgHtml = `<img class="img-fluid center-block image-checkered-back" src="${url}">`;
                break;
        }

        var btns: any = {};
        if (downloadBtn)
            btns.btnDownload = downloadButton(url, name);

        custom({
            message: `<div class="text-center">${msgHtml}</div>`,
            title: boxTitle,
            modalColor: 'modal-info',
            closeButton: true,
            size: 'large',
            buttons: btns
        });
    }

    /**
     * Displays images, audio and video in a dialog.
     * Define data-url, data-name, data-media inside the element
     * data-media should use 1 of audio,video,image
     */
    export function viewMediaFromElement(element: JQuery<HTMLElement>) {
        let name: string = element.data('name');
        let url: string = element.data('url');
        let media: string = element.data('media');
        let mediaType: MediaType = MediaType.Image;

        if (!url)
            url = String(element.attr('src'));

        if (!name) {
            // Extract the name of the file if no data name specified
            const idx = url.lastIndexOf('/');
            const dot = url.lastIndexOf('.');
            name = url.substr(idx + 1, dot - idx - 1);
        }

        if (!media)
            media = 'image';
        media = media.toLowerCase();

        switch (media) {
            case 'video':
                mediaType = MediaType.Video;
                break;
            case 'audio':
                mediaType = MediaType.Audio;
                break;
            default:
                mediaType = MediaType.Image;
        }

        viewMedia(url, name, mediaType);
    }

    /**
     * Download button configuration for Media file download
     */
    function downloadButton(mediaHref: string, fileName: string) {
        return {
            label: T('Download'),
            className: 'btn-success',
            callback: function() {
                const element = document.createElement('a');
                element.setAttribute('href', mediaHref);
                element.setAttribute('download', fileName);
                element.style.display = 'none';
                document.body.appendChild(element);
                element.click();
                document.body.removeChild(element);

                return true;
            }
        };
    }

    /**
     * Will show the billing upgrade dialog
     * Make sure it has been included in the page!
     */
    export function showBillUpgrade() {
        $('.upgradeOverlay').modal('show');
    }

    /**
     * Will show the billing upgrade dialog if an element is decorated with showBillUpgradeOnClick attribute
     * Make sure it has been included in the page!
     */
    export function showBillUpgradeOnClick(afterShownCallback?: Function) {
        const $billUpgradeReq = $('[showBillUpgradeOnClick]');

        if ($billUpgradeReq.is('option')) {
            const $parentSelect = $billUpgradeReq.closest('select');

            $parentSelect.on('change', function(e) {
                if ($('option:selected', this).is('[showBillUpgradeOnClick]')) {
                    showBillUpgrade();
                    if (afterShownCallback)
                        afterShownCallback();
                }
            });
            return;
        }

        $billUpgradeReq.on('click', function() {
            if ($('option:selected', this).is('[showBillUpgradeOnClick]')) {
                showBillUpgrade();
                if (afterShownCallback)
                    afterShownCallback();
            }
        });
    }

    /**
     * Hides any/all open dialog(s)
     */
    export function hide() {
        if ($('.modal.show').length > 0)
            ($('.modal.show') as any).modal('hide');
    }

    /**
     * Shows the loading dialog
     * @param withMessage Optionally shows a message in the loading dialog
     */
    export function showLoading(withMessage?: string) {
        const $preloader = $('.preloader');
        
        withMessage = withMessage ?? T('Loading...');
        $preloader.find('.appn-loading-text-container p.lead').text(withMessage);

        $preloader.show(0);
    }

    /**
     * Hides the loading dialog
     */
    export function hideLoading() {

        const $preloader = $('.preloader');

        $preloader.addClass('background-blur').delay(500).queue(function(next: () => void) {
            $preloader.removeClass('background-blur');
            next();
        }).hide(0);
    }

    /**
     * Shows the content spinner
     * @param $container required to inject content spinner into
     */
    export function showContentSpinner($container: JQuery<HTMLElement>) {
        const $contentSpinner = $('<div>', {
            class: 'content-spinner',
            title: T('Loading Content...')
        });

        const $spinnerElement = $('<div>', {
            class: 'appn-spinner spinner-border text-primary'
        });

        $spinnerElement.appendTo($contentSpinner);
        $contentSpinner.appendTo($container);
    }

    /**
     * Removes the content spinner
     * @param $container required to remove html for the content spinner
     */
    export function removeContentSpinner($container: JQuery<HTMLElement>) {
        $container.find('.content-spinner').remove();
    }

    /**
     * Shows the body spinner
     */
    export function showBodySpinner() {

        const $contentSpinner = $('<div>', {
            class: 'content-spinner',
            title: T('Loading Content...')
        });

        const $spinnerElement = $('<div>', {
            class: 'appn-spinner spinner-border text-primary'
        });

        $spinnerElement.appendTo($contentSpinner);
        $contentSpinner.appendTo($('body'));
    }

    /**
     * Removes the body spinner
     */
    export function removeBodySpinner() {
        $('body').find('.content-spinner').remove();
    }

    /**
     * Populates and hides or unhides the custom filename dropdowns
     */
    export function toggleAttachmentNamesSelect(attachmentNames: string[], formGroupFileName: JQuery<HTMLElement>, format?: string) {
        const fileNameSelect = $(`${format ? '#FileName.' + format : '#FileName'}`);
        fileNameSelect.html(emptyString);

        if (attachmentNames && attachmentNames.length > 0) {
            fileNameSelect.append(appn.elementBuilder.buildOptionsList(attachmentNames, 'Default'));
            formGroupFileName.show();
            if (format) // Bulk Export Failsafe
                $('input[name="SelectedFileName"]').val(String($(`.card-planejane.${format} .${format}#FileName`).val()));

            return;
        }

        formGroupFileName.hide();
        if (format) // Bulk Export Failsafe
            $('input[name="SelectedFileName"]').val(emptyString);
    }
}
